import { useState, useEffect, useRef } from 'react';
import './ScaleBar.css';

interface ScaleBarProps {
  /** Pixels per meter scale of the map */
  scale: number;
  /** Current zoom level */
  zoom: number;
}

/** Nice round numbers for scale bar distances (up to 1 billion meters for planetary maps) */
const NICE_DISTANCES = [
  0.1, 0.2, 0.5,
  1, 2, 5,
  10, 20, 50,
  100, 200, 500,
  1000, 2000, 5000,
  10000, 20000, 50000,
  100000, 200000, 500000,
  1000000, 2000000, 5000000,
  10000000, 20000000, 50000000,
  100000000, 200000000, 500000000,
  1000000000
];

/**
 * Formats a distance for display
 * Uses m for small distances, km for larger ones
 * @param meters - Distance in meters
 * @returns Formatted string with appropriate unit
 */
function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${meters / 1000}km`;
  }
  if (meters < 1) {
    return `${Math.round(meters * 100)}cm`;
  }
  return `${meters}m`;
}

/**
 * Scale bar component that adapts to zoom level
 * Shows a dynamic bar representing real-world distance
 */
function ScaleBar({ scale, zoom }: ScaleBarProps): JSX.Element {
  const [displayDistance, setDisplayDistance] = useState<number>(1);
  const [barWidth, setBarWidth] = useState<number>(100);
  const prevWidthRef = useRef<number>(100);

  // Target width range for the bar (in pixels)
  const MIN_BAR_WIDTH = 60;
  const MAX_BAR_WIDTH = 150;

  useEffect(() => {
    // Calculate pixels per meter at current zoom
    const pixelsPerMeter = scale * zoom;

    // Find the best distance that fits in our target width range
    let bestDistance = NICE_DISTANCES[0];
    let bestWidth = pixelsPerMeter * bestDistance;

    for (const distance of NICE_DISTANCES) {
      const width = pixelsPerMeter * distance;
      if (width >= MIN_BAR_WIDTH && width <= MAX_BAR_WIDTH) {
        bestDistance = distance;
        bestWidth = width;
        break;
      }
      if (width > MAX_BAR_WIDTH) {
        break;
      }
      bestDistance = distance;
      bestWidth = width;
    }

    // Clamp width to reasonable bounds
    bestWidth = Math.max(MIN_BAR_WIDTH, Math.min(MAX_BAR_WIDTH, bestWidth));

    setDisplayDistance(bestDistance);
    prevWidthRef.current = barWidth;
    setBarWidth(bestWidth);
  }, [scale, zoom, barWidth]);

  return (
    <div className="scale-bar">
      <div className="scale-bar-label">{formatDistance(displayDistance)}</div>
      <div
        className="scale-bar-track"
        style={{ width: `${barWidth}px` }}
      >
        <div className="scale-bar-fill" />
        <div className="scale-bar-ticks">
          <div className="scale-bar-tick scale-bar-tick--start" />
          <div className="scale-bar-tick scale-bar-tick--middle" />
          <div className="scale-bar-tick scale-bar-tick--end" />
        </div>
      </div>
    </div>
  );
}

export default ScaleBar;
